import { FC, forwardRef } from 'react';
import { DialogProps } from '@toolpad/core/useDialogs';
import {
  Dialog,
  Slide,
  Box,
  DialogContent,
  Stack,
  Typography,
  Button,
  useMediaQuery,
  CircularProgress,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { useTheme } from '@mui/material/styles';
import { useDialogs } from '@toolpad/core/useDialogs';
import { useTranslation } from 'react-i18next';

import { CreateArtworkDialog } from '@customer/features/artworks-stepper';
import { useCurrentVenueSlug, useGetVenue } from '@customer/features/venues';
import { ViewNavigationBar } from '@components';

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<unknown>;
  },
  ref: React.Ref<unknown>
) {
  return (
    <Slide
      direction="up"
      easing={{
        enter: 'cubic-bezier(0.66, 0, 0.34, 1)',
        exit: 'linear',
      }}
      ref={ref}
      {...props}
    />
  );
});

export const ArtworkTypeSelectionDialog: FC<DialogProps> = ({
  open,
  onClose,
}) => {
  const theme = useTheme();
  const dialogs = useDialogs();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const venueSlug = useCurrentVenueSlug();
  const { data: venue, isPending } = useGetVenue(venueSlug);

  const availableArtworkTypes = venue?.available_artwork_types ?? [];
  const isPhotoTypeAvailable = availableArtworkTypes.includes('PHOTO');
  const isLogoTypeAvailable = availableArtworkTypes.includes('LOGO');

  const handleOpenPhotoTinDialog = async () => {
    onClose();
    await dialogs.open(CreateArtworkDialog, {
      artworkType: 'PHOTO',
    });
  };

  const handleOpenLogoTinDialog = async () => {
    onClose();
    await dialogs.open(CreateArtworkDialog, {
      artworkType: 'LOGO',
    });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={() => onClose()}
        fullWidth={true}
        maxWidth="lg"
        fullScreen={fullScreen}
        scroll="paper"
        slots={{
          transition: Transition,
        }}
        slotProps={{
          paper: {
            sx: {
              height: '100%',
            },
          },
        }}
      >
        <ViewNavigationBar
          onClose={() => onClose()}
          title={t('selectYourArtworkType')}
        />
        <DialogContent
          sx={{
            position: 'relative',
            height: '100%',
            minHeight: {
              md: 'calc(100vh - 200px)',
            },
            padding: 0,
          }}
        >
          {isPending && (
            <Stack
              sx={{ height: '100%' }}
              justifyContent="center"
              alignItems="center"
            >
              <CircularProgress />
            </Stack>
          )}

          {!isPending && !isPhotoTypeAvailable && !isLogoTypeAvailable && (
            <Stack
              sx={{ height: '100%', padding: 4 }}
              justifyContent="center"
              alignItems="center"
            >
              <Typography textAlign="center">
                {t('noArtworkTypesAvailable')}
              </Typography>
            </Stack>
          )}

          {!isPending && (isPhotoTypeAvailable || isLogoTypeAvailable) && (
            <Box
              sx={{
                display: 'flex',
                flexGrow: 0,
                flexDirection: { xs: 'column', md: 'row' },
                height: '100%',
              }}
            >
              {isPhotoTypeAvailable && (
                <Stack
                  direction="column"
                  flexGrow={1}
                  justifyContent="center"
                  alignItems="center"
                  spacing={{ xs: 5, lg: 15 }}
                  onClick={handleOpenPhotoTinDialog}
                  sx={{
                    backgroundColor: '#ECF1FC',
                    paddingTop: 8,
                    paddingBottom: 8,
                    cursor: 'pointer',
                  }}
                >
                  <Box
                    sx={{
                      flexDirection: 'column',
                      textAlign: 'center',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 18,
                        fontWeight: 400,
                      }}
                    >
                      {t('createYour')}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 22,
                          fontWeight: 600,
                          marginLeft: 2,
                          marginRight: 2,
                        }}
                      >
                        NIVEA
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 22,
                          fontWeight: 400,
                          textTransform: 'uppercase',
                        }}
                      >
                        {t('photoTin')}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    component="img"
                    src="/photo_tin_teaser.png"
                    alt="NIVEA Photo Tin"
                    sx={{ width: '100%', maxWidth: 300, height: 'auto' }}
                  />
                  <Button variant="contained">{t('createNow')}</Button>
                </Stack>
              )}
              {isLogoTypeAvailable && (
                <Stack
                  direction="column"
                  flexGrow={1}
                  justifyContent="center"
                  alignItems="center"
                  spacing={{ xs: 5, lg: 15 }}
                  onClick={handleOpenLogoTinDialog}
                  sx={{
                    backgroundColor: '#ECF1FC80',
                    paddingTop: 8,
                    paddingBottom: 8,
                    cursor: 'pointer',
                  }}
                >
                  <Box sx={{ flexDirection: 'column', textAlign: 'center' }}>
                    <Typography
                      sx={{
                        fontSize: 18,
                        fontWeight: 400,
                      }}
                    >
                      {t('createYour')}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 22,
                          fontWeight: 600,
                          marginLeft: 2,
                          marginRight: 2,
                        }}
                      >
                        NIVEA
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 22,
                          fontWeight: 400,
                          textTransform: 'uppercase',
                        }}
                      >
                        {t('logoTin')}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    component="img"
                    src={
                      currentLanguage === 'de' || currentLanguage === 'de-DE'
                        ? '/logo_tin_teaser_de.png'
                        : '/logo_tin_teaser_en.png'
                    }
                    alt="NIVEA Logo Tin"
                    sx={{ width: '100%', maxWidth: 300, height: 'auto' }}
                  />
                  <Button variant="contained">{t('createNow')}</Button>
                </Stack>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
